import { useCallback, useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { getFirebaseAuth, isFirebaseConfigured } from './firebase';
import { authorize, clearAuthorizationCache, type AuthorizationDecision } from './nexus';

export type NexusAuthState = {
  user: User | null;
  decision: AuthorizationDecision | null;
  loading: boolean;
  error: string | null;
  revalidate: () => Promise<void>;
  signOut: () => Promise<void>;
};

export function useNexusAuthorization(): NexusAuthState {
  const [user, setUser] = useState<User | null>(null);
  const [decision, setDecision] = useState<AuthorizationDecision | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const runAuthorize = useCallback(async (u: User | null) => {
    if (!u) {
      setDecision(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const idToken = await u.getIdToken();
      const result = await authorize(idToken);
      setDecision(result);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Authorization failed';
      setError(message);
      setDecision(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(getFirebaseAuth(), (u) => {
      setUser(u);
      void runAuthorize(u);
    });
    return unsub;
  }, [runAuthorize]);

  useEffect(() => {
    if (!user) return;
    const onFocus = () => void runAuthorize(user);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, runAuthorize]);

  const revalidate = useCallback(async () => {
    clearAuthorizationCache();
    await runAuthorize(user);
  }, [runAuthorize, user]);

  const signOut = useCallback(async () => {
    clearAuthorizationCache();
    if (isFirebaseConfigured) {
      await getFirebaseAuth().signOut();
    }
    setUser(null);
    setDecision(null);
  }, []);

  return { user, decision, loading, error, revalidate, signOut };
}
